import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Modal } from 'antd'
import classNames from 'classnames'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { dictionariesQueries } from '@/api/dictionaries/dictionariesQueries'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import Loader from '@/components/Atoms/Loader/Loader'
import SortableColumnTitle from '@/components/SortableColumnTitle/SortableColumnTitle'
import type { ContractInterface } from '@/types'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import { contractsQueries } from './api/contractsQueries'
import ContractCreationButton from './ContractCreationButton/ContractCreationButton'
import {
  contractEditFormSchema,
  type ContractEditFormType,
  getPaymentTypeOption,
} from './Contracts.config'
import cn from './Contracts.module.scss'
import {
  CONTRACTS_COLUMN_WIDTHS,
  getContractsColumns,
  type RenderContractsSortTitle,
} from './contractsColumns'
import ContractsFilters from './ContractsFilters/ContractsFilters'
import { useContractsAccess } from './hooks/useContractsAccess'
import { useContractsList } from './hooks/useContractsList'
import { useUpdateContractMutation } from './hooks/useUpdateContractMutation'
import {
  getContractPayloadError,
  toUpdateContractRequest,
} from './lib/contractEditForm'

function Contracts() {
  const { organisationId, isSelectedOrganisation, canEdit } =
    useContractsAccess()

  const [currentlyEditableRowKey, setCurrentlyEditableRowKey] = useState('')
  const queryClient = useQueryClient()
  const updateContract = useUpdateContractMutation()
  const { filters, setFilters, getSortOrder, toggleSort, contractsQuery } =
    useContractsList(organisationId)
  const contracts = contractsQuery.data ?? []
  const paymentTypesQuery = useQuery({
    ...dictionariesQueries.paymentTypes(),
    enabled: currentlyEditableRowKey !== '',
  })

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContractEditFormType>({
    shouldFocusError: false,
    resolver: zodResolver(contractEditFormSchema),
  })
  const [modal, contextHolder] = Modal.useModal()

  const editContract = (contract: ContractInterface, rowKey: string) => {
    setCurrentlyEditableRowKey(rowKey)
    reset({
      number: contract.number || '',
      isActive: !!contract.isActive,
      paymentType: contract.paymentTypeModel
        ? getPaymentTypeOption(contract.paymentTypeModel)
        : undefined,
    })
  }

  const cancelEdit = () => {
    setCurrentlyEditableRowKey('')
    reset()
  }

  const submitContract = (rowKey: string, formValues: ContractEditFormType) => {
    if (updateContract.isPending) return
    const originalContract = contracts.find((c) => c.id === rowKey)
    if (!originalContract) {
      toast.error('Договор не найден')
      return
    }
    if (!originalContract.currencyModel?.id) {
      toast.error('Недостаточно данных договора (валюта)')
      return
    }

    const payload = toUpdateContractRequest(
      formValues,
      originalContract,
      originalContract.currencyModel.id
    )
    const payloadError = getContractPayloadError(payload)
    if (payloadError) {
      toast.error(payloadError)
      return
    }

    updateContract.mutate(
      { organisationId, contractId: rowKey, payload },
      {
        onSuccess: () => {
          toast.success('Договор успешно обновлен')
          setCurrentlyEditableRowKey('')
          reset()
        },
        onError: (err) => handleApiError(err, modal),
      }
    )
  }

  const saveContract = (rowKey: string) =>
    handleSubmit((formValues) => submitContract(rowKey, formValues))()

  const renderSortTitle: RenderContractsSortTitle = (field, title) => (
    <SortableColumnTitle
      title={title}
      order={getSortOrder(field)}
      onToggle={() => toggleSort(field)}
      isOrganisationSelected={isSelectedOrganisation}
    />
  )

  const renderContent = () => {
    if (contractsQuery.isLoading) return <Loader size={20} />
    if (contractsQuery.isError)
      return (
        <p className={cn.emptyContractsTableMessage}>
          {getGeneralErrorMessage(contractsQuery.error)}{' '}
          <Button type="link" onClick={() => contractsQuery.refetch()}>
            Повторить
          </Button>
        </p>
      )
    return (
      <AtomTable
        ariaLabel="Договоры"
        data={contracts}
        columns={getContractsColumns({
          editingKey: currentlyEditableRowKey,
          canEdit,
          isOrganisationSelected: isSelectedOrganisation,
          paymentTypes: paymentTypesQuery.data ?? [],
          paymentTypesLoading: paymentTypesQuery.isLoading,
          control,
          register,
          errors,
          renderSortTitle,
          onEdit: editContract,
          onSave: saveContract,
          onCancel: cancelEdit,
        })}
        getRowId={(contract) => contract.id}
        columnWidths={CONTRACTS_COLUMN_WIDTHS}
        isOrganisationSelected={isSelectedOrganisation}
        isStale={contractsQuery.isPlaceholderData || updateContract.isPending}
      />
    )
  }

  return (
    <>
      {contextHolder}
      <section
        className={classNames(
          cn.contracts,
          isSelectedOrganisation && cn.selectedOrganisation
        )}
      >
        <div className={cn.inner}>
          {canEdit && (
            <ContractCreationButton
              className={cn.createContractButtonContainer}
              onCreated={() =>
                queryClient.invalidateQueries({
                  queryKey: contractsQueries.lists(),
                })
              }
            />
          )}
          <ContractsFilters
            value={filters}
            onChange={setFilters}
            isOrganisationSelected={isSelectedOrganisation}
          />
          {renderContent()}
        </div>
      </section>
    </>
  )
}

export default Contracts
